/* eslint-disable no-undef */
const loadableReady = require('@loadable/component').loadableReady;
const ReactDOM = require('react-dom/client');
const CookieConsent = require('vanilla-cookieconsent');
const { createPageViewTracker } = require('./src/analytics/page-view');
const { createConsentPublisher } = require('./src/analytics/consent-update');
const {
  createShowCookiePreferences,
} = require('./src/consent/show-preferences');

require('vanilla-cookieconsent/dist/cookieconsent.css');

const GET_RESPONSE_ANALYTICS_URL =
  'https://an.gr-wcon.com/script/85dd5074-df88-416d-b567-5cb7ffd5a3fd/ga.js';
const COOKIE_CATEGORIES = {
  necessary: 'necessary',
  analytics: 'analytics',
  marketing: 'marketing',
};

const updateGoogleConsentMode = () => {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') {
    return;
  }

  window.gtag('consent', 'update', {
    analytics_storage: CookieConsent.acceptedCategory(
      COOKIE_CATEGORIES.analytics,
    )
      ? 'granted'
      : 'denied',
    ad_storage: CookieConsent.acceptedCategory(COOKIE_CATEGORIES.marketing)
      ? 'granted'
      : 'denied',
    ad_user_data: CookieConsent.acceptedCategory(COOKIE_CATEGORIES.marketing)
      ? 'granted'
      : 'denied',
    ad_personalization: CookieConsent.acceptedCategory(
      COOKIE_CATEGORIES.marketing,
    )
      ? 'granted'
      : 'denied',
  });
};

const loadGetResponseAnalytics = () => {
  if (
    typeof window === 'undefined' ||
    typeof window.GrTracking !== 'undefined' ||
    typeof document === 'undefined'
  ) {
    return;
  }

  (function (m, o, n, t, e, r, _) {
    m.__GetResponseAnalyticsObject = e;
    m[e] =
      m[e] ||
      function () {
        (m[e].q = m[e].q || []).push(arguments);
      };
    r = o.createElement(n);
    _ = o.getElementsByTagName(n)[0];
    r.async = 1;
    r.src = t;
    r.setAttribute('crossorigin', 'use-credentials');
    _.parentNode.insertBefore(r, _);
  })(window, document, 'script', GET_RESPONSE_ANALYTICS_URL, 'GrTracking');
};

const hasStatisticsConsent = () =>
  typeof window !== 'undefined' &&
  CookieConsent.acceptedCategory(COOKIE_CATEGORIES.analytics);

const hasMarketingConsent = () =>
  typeof window !== 'undefined' &&
  CookieConsent.acceptedCategory(COOKIE_CATEGORIES.marketing);

const dataLayer = () => {
  window.dataLayer = window.dataLayer || [];

  return window.dataLayer;
};

// One page_view per client-side navigation; GTM's own Google tag counts the
// initial load and the page where consent is granted. See
// src/analytics/page-view.ts for the rules and the GTM dependency.
const pageViews = createPageViewTracker({
  hasConsent: hasStatisticsConsent,
  dataLayer,
});

// The Google tag fires on the consent event and sends its own page view of the
// page the visitor is on; the next route update on that path is not a view.
const rememberConsentPageView = () => {
  if (hasStatisticsConsent()) {
    pageViews.markCounted(window.location);
  }
};

// Runs `callback` after React has committed and painted the new page, so the
// Head API has written the new <title> before GTM reads document.title for the
// event. Same approach as gatsby-plugin-google-gtag.
const afterRender = (callback) => {
  if ('requestAnimationFrame' in window) {
    requestAnimationFrame(() => {
      requestAnimationFrame(callback);
    });
  } else {
    setTimeout(callback, 32);
  }
};

// The consent decision as an event GTM can trigger on; Consent Mode alone gives
// it nothing to fire after the banner. See src/analytics/consent-update.ts.
const publishConsentUpdate = createConsentPublisher({
  hasDecided: () => CookieConsent.validConsent(),
  hasAnalyticsConsent: hasStatisticsConsent,
  hasMarketingConsent,
  dataLayer,
});

// Everything that has to happen whenever the consent state may have changed:
// on the banner's first answer, on every later answer, and once run() has
// resolved, when a returning visitor's stored decision has been restored and
// has to reach GTM on this page load too.
const applyConsentState = () => {
  updateGoogleConsentMode();
  publishConsentUpdate();
  rememberConsentPageView();
  syncStatisticsTrackingWithConsent();
};

const syncStatisticsTrackingWithConsent = () => {
  if (typeof window === 'undefined') {
    return;
  }

  if (hasStatisticsConsent()) {
    loadGetResponseAnalytics();
  }
};

const initializeCookieConsent = () => {
  if (typeof window === 'undefined' || window.__cookieConsentInitialized) {
    return;
  }

  const ready = CookieConsent.run({
    mode: 'opt-in',
    autoShow: true,
    guiOptions: {
      consentModal: {
        layout: 'box inline',
        position: 'bottom right',
        equalWeightButtons: true,
        flipButtons: false,
      },
      preferencesModal: {
        layout: 'box',
        position: 'right',
        equalWeightButtons: true,
        flipButtons: false,
      },
    },
    categories: {
      [COOKIE_CATEGORIES.necessary]: {
        enabled: true,
        readOnly: true,
      },
      [COOKIE_CATEGORIES.analytics]: {
        autoClear: {
          // Reload after a withdrawal. Deleting the cookies is not enough: the
          // GetResponse script is already running and would re-create
          // gaVisitorUuid and keep sending for the rest of the page load, which
          // would make the banner's promise ("the measurement stops") false.
          reloadPage: true,
          // Every analytics cookie the site sets: Google Analytics 4 and
          // GetResponse. Withdrawal has to remove all of them.
          cookies: [
            { name: /^_ga/ },
            { name: '_gid' },
            { name: 'gaVisitorUuid' },
          ],
        },
      },
      [COOKIE_CATEGORIES.marketing]: {},
    },
    onFirstConsent: applyConsentState,
    onConsent: applyConsentState,
    onChange: applyConsentState,
    language: {
      default: 'en',
      translations: {
        en: {
          consentModal: {
            title: 'We use cookies',
            description:
              'We use essential cookies to keep the site working and optional analytics and marketing cookies to understand traffic and improve campaigns. Optional cookies are enabled only after consent.',
            acceptAllBtn: 'Accept all',
            acceptNecessaryBtn: 'Reject optional',
            showPreferencesBtn: 'Manage preferences',
            footer:
              '<a href="/privacy-policy/">Privacy policy</a><a href="/cookies-policy/">Cookies policy</a>',
          },
          preferencesModal: {
            title: 'Cookie preferences',
            acceptAllBtn: 'Accept all',
            acceptNecessaryBtn: 'Reject optional',
            savePreferencesBtn: 'Save preferences',
            closeIconLabel: 'Close modal',
            sections: [
              {
                title: 'Cookie usage',
                description:
                  'You can decide which optional cookies may be stored on your device while using the Website. We use cookies to ensure the proper operation of the Website, improve its performance, analyze traffic, and support our marketing activities. Necessary cookies remain enabled because the Website depends on them to function properly.',
              },
              {
                title: 'Necessary cookies',
                description:
                  'One first-party cookie, cc_cookie, stores your decision about cookies: which categories you accepted and when. It holds nothing else about you and expires after 6 months. It is what lets us respect your choice without asking again on every visit, so it cannot be switched off.',
                linkedCategory: COOKIE_CATEGORIES.necessary,
              },
              {
                title: 'Analytics cookies',
                description:
                  'Google Analytics 4 (cookies _ga and _ga_*, up to 2 years) and GetResponse web analytics (cookie gaVisitorUuid) count visits and page views and show us which pages are read. Nothing is sent to either service until you accept this category. If you withdraw consent later, the measurement stops and these cookies are removed.',
                linkedCategory: COOKIE_CATEGORIES.analytics,
              },
              {
                title: 'Marketing cookies',
                description:
                  'These cookies are used to support our marketing activities, including measuring the effectiveness of campaigns, tracking conversions, and delivering more relevant promotional content. They may be used by advertising and attribution tools configured through Google Tag Manager.',
                linkedCategory: COOKIE_CATEGORIES.marketing,
              },
              {
                title: 'More information',
                description:
                  'See our <a href="/privacy-policy/">privacy policy</a> and <a href="/cookies-policy/">cookies policy</a> for details.',
              },
            ],
          },
        },
      },
    },
  }).then(applyConsentState);

  ready
    .then(() => {
      // Only now can the banner build its preferences modal. Before this, and in
      // browsers where run() bails out (hideFromBots: bot user agents,
      // WebDriver), showPreferences() throws — see src/consent/show-preferences.ts.
      window.showCookiePreferences = createShowCookiePreferences(CookieConsent);
    })
    .catch((cause) => {
      // Withdrawing consent is a right, not a nice-to-have (GDPR Art. 7(3)), so
      // a failed run() must not leave the footer button silently dead. The
      // wrapper is installed anyway: clicking it then logs one warning.
      window.showCookiePreferences = createShowCookiePreferences(CookieConsent);
      // eslint-disable-next-line no-console
      console.warn('The cookie consent banner failed to initialise.', cause);
    });

  // Set synchronously and not inside the promise: it guards against a second
  // run() while the first is still resolving.
  window.__cookieConsentInitialized = true;
};

exports.onClientEntry = () => {
  initializeCookieConsent();
};

exports.onRouteUpdate = ({ location, prevLocation }) => {
  initializeCookieConsent();

  // Gatsby passes prevLocation only for client-side navigations. The initial
  // load has none and is counted by GTM's Google tag; remembering its path keeps
  // an anchor click on the landing page from being pushed as a second view.
  if (!prevLocation) {
    pageViews.markCounted(location);

    return;
  }

  afterRender(() => {
    pageViews.track(location);
  });
};

exports.replaceHydrateFunction = () => {
  return (element, container, callback) => {
    loadableReady(() => {
      ReactDOM.createRoot(container).render(element);

      if (callback) {
        requestIdleCallback(callback);
      }
    });
  };
};
